import { api } from "./icfApi";
import { icfUtilsURL, icfPlatformAgentsURL } from "../../constant";

/**
 * Converts a File to a base64 string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      const base64 = result.split(",")[1];
      resolve(base64);
    };

    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Log ICF processing step (Upload / Digitize / Generate Section, etc.)
 * Logging failures must never break the main workflow, so errors are only console-logged.
 */
export const logICFStep = async ({
  job_id,
  step_name,
  file_id,
  status,
  message,
  updated_by,
  created_timestamp,
  updated_timestamp,
}) => {
  try {
    await api.post(`${icfUtilsURL}/icf_logs/run`, {
      job_id,
      step_name,
      file_id,
      status,
      message,
      updated_by,
      created_timestamp,
      updated_timestamp,
    });
  } catch (error) {
    console.error("ICF Log Step failed:", error);
    // not re-thrown intentionally — logging should never block the actual flow
  }
};

/**
 * ICF Planner Trigger
 */
export const triggerICFPlanner = async (payload, signal, logMeta = {}) => {
  const created_timestamp = new Date().toISOString();
  try {
    const response = await api.post(`${icfPlatformAgentsURL}/generate-icf/run`, payload, { signal });
    logICFStep({
      job_id: logMeta.job_id,
      step_name: `Generate Section: Generated text for ${logMeta?.section_name}`,
      file_id: logMeta.file_id,
      status: "SUCCESS",
      message: response?.data?.response?.message || "Section generated successfully",
      updated_by: logMeta.updated_by,
      created_timestamp,
      updated_timestamp: new Date().toISOString(),
    });
    return response.data.response;
  } catch (error) {
    if (axios.isCancel?.(error) || error.name === "CanceledError" || err.code === "ERR_CANCELED") {
      console.warn("ICF Planner Generation request aborted");
      return; // exit silently
    }
    console.error("ICF Planner Trigger failed:", error);
    logICFStep({
      job_id: logMeta.job_id,
      step_name: `Generate Section: Generated text for ${logMeta?.section_name}`,
      file_id: logMeta.file_id,
      status: "FAILURE",
      message: error?.response?.data?.message || error?.message || "Section generation failed",
      updated_by: logMeta.updated_by,
      created_timestamp,
      updated_timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Upload ICF File
 */

export const uploadICFFile = async (payload, logMeta = {}) => {
  const created_timestamp = new Date().toISOString();
  try {
    const response = await api.post(`${icfUtilsURL}/icf-file-upload/run`, payload);
    logICFStep({
      job_id: response?.data?.response?.data?.job_id,
      step_name: "Upload",
      file_id: response?.data?.response?.data?.file_id,
      status: "SUCCESS",
      message: response?.data?.response?.message || "File uploaded successfully",
      updated_by: logMeta.updated_by,
      created_timestamp,
      updated_timestamp: new Date().toISOString(),
    });
    return response.data.response;
  } catch (error) {
    console.error("ICF File Upload failed:", error);
    logICFStep({
      job_id: logMeta.job_id,
      step_name: "Upload",
      file_id: logMeta.file_id,
      status: "FAILURE",
      message: error?.response?.data?.message || error?.message || "File upload failed",
      updated_by: logMeta.updated_by,
      created_timestamp,
      updated_timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Digitize ICF File
 */
export const digitizeICFFile = async (payload, signal, logMeta = {}) => {
  const created_timestamp = new Date().toISOString();
  try {
    const response = await api.post(`${icfUtilsURL}/icf-file-digitization/run`, payload, { signal });
    logICFStep({
      job_id: logMeta.job_id,
      step_name: "Digitize",
      file_id: logMeta.file_id,
      status: "SUCCESS",
      message: response?.data?.response?.message || "File digitized successfully",
      updated_by: logMeta.updated_by,
      created_timestamp,
      updated_timestamp: new Date().toISOString(),
    });
    return response.data.response;
  } catch (error) {
    if (axios.isCancel?.(error) || error.name === "CanceledError" || err.code === "ERR_CANCELED") {
      console.warn("Digitization request aborted");
      return; // exit silently
    }
    console.error("ICF File Digitization failed:", error);
    logICFStep({
      job_id: logMeta.job_id,
      step_name: "Digitize",
      file_id: logMeta.file_id,
      status: "FAILURE",
      message: error?.response?.data?.message || error?.message || "Digitization failed",
      updated_by: logMeta.updated_by,
      created_timestamp,
      updated_timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get ICF Digitization Percentage
 */
export const getICFDigitizationPercent = async (payload) => {
  try {
    const response = await api.post(`${icfUtilsURL}/get-digitization-percent/run`, payload);
    return response.data.response;
  } catch (error) {
    console.error("Get ICF Digitization Percent failed:", error);
    throw error;
  }
};

/**
 * Save Selected Section
 */
export const saveICFSelectedSection = async (payload) => {
  try {
    const response = await api.post(`${icfUtilsURL}/save-selected-section/run`, payload);
    return response.data.response;
  } catch (error) {
    console.error("Save Selected Section failed:", error);
    throw error;
  }
};

/**
 * Get Section Data
 */
export const getICFSectionData = async (payload) => {
  try {
    const response = await api.post(`${icfUtilsURL}/get-section-data/run`, payload);
    return response.data.response;
  } catch (error) {
    console.error("Get Section Data failed:", error);
    throw error;
  }
};

/**
 * Get Template List
 */
export const getICFTemplateList = async (payload) => {
  try {
    const response = await api.post(`${icfUtilsURL}/get-template-list/run`, payload);
    return response.data.response;
  } catch (error) {
    console.error("Get ICF Template List failed:", error);
    throw error;
  }
};

/**
 * Get Section Configured Dataset
 */
export const getICFSectionConfiguredDataset = async (payload) => {
  try {
    const response = await api.post(`${icfUtilsURL}/get-section-configured-dataset/run`, payload);
    return response.data.response;
  } catch (error) {
    console.error("Get ICF Section Configured Dataset failed:", error);
    throw error;
  }
};

/**
 * Save Generated Log
 */
export const saveICFGeneratedLog = async (payload) => {
  try {
    const response = await api.post(`${icfUtilsURL}/save-generated-log/run`, payload);
    return response.data.response;
  } catch (error) {
    console.error("Save ICF Generated Log failed:", error);
    throw error;
  }
};

/**
 * Get Generated Log
 */
export const getICFGeneratedLog = async (payload) => {
  try {
    const response = await api.post(`${icfUtilsURL}/get-log-generated/run`, payload);
    return response.data.response;
  } catch (error) {
    console.error("Get ICF Generated Log failed:", error);
    throw error;
  }
};

/**
 * Get Context Library
 */
export const getICFContextLib = async (payload, signal, logMeta = {}) => {
  const created_timestamp = new Date().toISOString();
  try {
    const response = await api.post(`${icfUtilsURL}/get-context-lib/run`, payload, { signal });
    logICFStep({
      job_id: logMeta.job_id,
      step_name: `Generate Section: Generated text for ${logMeta?.section_name}`,
      file_id: logMeta.file_id,
      status: "SUCCESS",
      message: "Context library fetched successfully",
      updated_by: logMeta.updated_by,
      created_timestamp,
      updated_timestamp: new Date().toISOString(),
    });
    return response.data.response;
  } catch (error) {
    if (axios.isCancel?.(error) || error.name === "CanceledError" || err.code === "ERR_CANCELED") {
      console.warn("ICF Context Library request aborted");
      return; // exit silently
    }
    console.error("Get ICF Static Context Library failed:", error);
    logICFStep({
      job_id: logMeta.job_id,
      step_name: `Generate Section: Generated text for ${logMeta?.section_name}`,
      file_id: logMeta.file_id,
      status: "FAILURE",
      message: error?.response?.data?.message || error?.message || "Context library fetch failed",
      updated_by: logMeta.updated_by,
      created_timestamp,
      updated_timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get Study Procedure
 */
export const getStudyProcedure = async (payload, signal) => {
  try {
    const response = await api.post(`${icfUtilsURL}/get-study-procedure-content/run`, payload, { signal });
    return response.data.response;
  } catch (error) {
    if (axios.isCancel?.(error) || error.name === "CanceledError" || err.code === "ERR_CANCELED") {
      console.warn("ICF Study Procedure request aborted");
      return; // exit silently
    }
    console.error("Get ICF Study Procedure failed:", error);
    throw error;
  }
};

/**
 * Add Attributes
 */
export const addAttributes = async (payload, signal) => {
  try {
    const response = await api.post(`${icfUtilsURL}/add_attributes/run`, payload, { signal });
    return response.data.response;
  } catch (error) {
    if (axios.isCancel?.(error) || error.name === "CanceledError" || err.code === "ERR_CANCELED") {
      console.warn("Get add_attributes request aborted");
      return; // exit silently
    }
    console.error("Get add_attributes failed:", error);
    throw error;
  }
};

/**
 * Get All Protocol Versions
 */
export const getAllProtocolVersions = async (payload, signal) => {
  try {
    const response = await api.post(`${icfUtilsURL}/get_all_protocol_versions/run`, payload, { signal });
    return response.data.response;
  } catch (error) {
    if (axios.isCancel?.(error) || error.name === "CanceledError" || err.code === "ERR_CANCELED") {
      console.warn("Get get_all_protocol_versions request aborted");
      return; // exit silently
    }
    console.error("Get get_all_protocol_versions failed:", error);
    throw error;
  }
};

/**
 * Get Protocol Details
 */
export const getProtocolDetails = async (payload, signal) => {
  try {
    const response = await api.post(`${icfUtilsURL}/get_protocol_details/run`, payload, { signal });
    return response.data.response;
  } catch (error) {
    if (axios.isCancel?.(error) || error.name === "CanceledError" || err.code === "ERR_CANCELED") {
      console.warn("Get get_protocol_details request aborted");
      return; // exit silently
    }
    console.error("Get get_protocol_details failed:", error);
    throw error;
  }
};


